/**
 * How a set-password attempt is made, classified and reported — everything
 * between the user's submit and the state the dialog renders from.
 *
 * Split out of `set-password.tsx` so that file is composition and presentation
 * (style §3.2: stateful extraction moves DOWN into a `use-*` module, never
 * sideways into a bucket). Nothing here touches the DOM or knows what the
 * confirm looks like; it owns the POST, the eight-arm taxonomy and the three
 * refs that make the write exactly-once.
 *
 * The taxonomy deliberately does NOT move to `password-field.ts` alongside the
 * shared value predicates. `classifyError` and `messageFor` diverge from
 * `password-login.tsx` on purpose — 401 is a plain arm here and an ambiguous
 * one on the ceremony routes — and merging them would force one behaviour on
 * two surfaces that have deliberately different ones.
 */
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

import type { AppConfig } from '@/config';

import { AuthFetchError, AuthNetworkError, postAuthJson, reportUnexpected } from './auth-fetch';
import { isRecord } from './is-record';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from './password-constants';
import { clearPasswordSync, validatePassword } from './password-field';

const INVALID_STATUS = 400;
const UNAUTHORIZED_STATUS = 401;
const RATE_LIMIT_STATUS = 429;

export type SetPasswordError =
  | 'empty'
  | 'too-short'
  | 'too-long'
  | 'invalid'
  | 'unauthorized'
  | 'rate-limited'
  | 'network'
  | 'generic';

/**
 * Classify off the TYPED status, never a message string — Safari's transport
 * failure is `TypeError: Load failed`, which carries no distinguishing text.
 *
 * 401 is a plain arm here, and that is the difference from `add-passkey.tsx`.
 * The add-credential routes overload 401 for a ceremony that failed while the
 * session was still valid, so that surface has to ask `refresh()` which of the
 * two it met. `handleSetPassword` (apps/api/src/routes/password-auth.ts)
 * answers 401 only for `req.auth === undefined`, so the status already says
 * "expired" — and a refresh would confirm it by unmounting the account menu out
 * from under the alert the user is being asked to read.
 */
function classifyError(err: unknown): SetPasswordError {
  if (err instanceof AuthNetworkError) return 'network';
  if (err instanceof AuthFetchError) {
    if (err.status === INVALID_STATUS) return 'invalid';
    if (err.status === UNAUTHORIZED_STATUS) return 'unauthorized';
    if (err.status === RATE_LIMIT_STATUS) return 'rate-limited';
  }
  /* Everything else — an unrecognised status, a body that did not confirm the
     set — folds into one message rather than inventing a remedy per state. */
  return 'generic';
}

export function messageFor(error: SetPasswordError): string {
  if (error === 'empty') return 'Enter a new password.';
  /* Composed from the shared constant, never a literal: a hard-coded number
     survives a change to the contract and tells the user a bound the form no
     longer enforces (and which the server would still apply). */
  if (error === 'too-short') {
    return `Password must be at least ${String(PASSWORD_MIN_LENGTH)} characters.`;
  }
  if (error === 'too-long') {
    return `Password must be at most ${String(PASSWORD_MAX_LENGTH)} characters.`;
  }
  if (error === 'invalid') return 'That password was rejected. Choose a different one.';
  if (error === 'unauthorized') return 'Your session has expired. Sign in again.';
  /* No duration. Unlike `/auth/password/login`, this route has a single 429
     producer — `handleSetPassword` never touches the ADR-0011 consecutive-ban,
     so `@fastify/rate-limit` is the only one — but its reset window is a server
     constant on the far side of a forbidden import edge. Any number named here
     is a guess the form cannot keep true. */
  if (error === 'rate-limited') return 'Too many attempts. Try again later.';
  if (error === 'network') return 'Network error - try again.';
  return 'Could not set your password. Try again.';
}

/**
 * A 200 is not a success — the envelope has to say so.
 *
 * `postAuthJson` resolves `undefined` for a body-less response, so a 204 fails
 * here on the first guard rather than reaching the confirmation.
 */
function confirmsSet(payload: unknown): boolean {
  if (!isRecord(payload)) return false;
  const data = payload['data'];
  return isRecord(data) && data['set'] === true;
}

/**
 * Stop-not-retry arms: the user is told not to send the value again, so the
 * plaintext does not outlive the attempt. Retryable arms keep it.
 */
function spendsValue(error: SetPasswordError): boolean {
  return error === 'rate-limited' || error === 'unauthorized';
}

/**
 * Classify and report defects. It does NOT spend the value — that happens in
 * `runSetPasswordSubmit`, behind the generation check.
 *
 * Clearing here would run before that check and so on behalf of an attempt the
 * user may already have dismissed: Cancel, reopen, type a new password, and the
 * abandoned request's 401 wipes what was just typed, with the error correctly
 * suppressed and therefore nothing on screen to explain it.
 */
function failureFor(err: unknown): SetPasswordError {
  const classified = classifyError(err);
  /* Transport outcomes return immediately; only a throwable that is neither
     escapes to the host, asynchronously, after this state settles. */
  reportUnexpected(err);
  return classified;
}

export interface SetPasswordSubmitOpts {
  readonly config: AppConfig;
  readonly credential: { readonly password: string; readonly setPassword: (next: string) => void };
  readonly onSaved: () => void;
  /** A save that landed after the user dismissed it. The credential changed. */
  readonly onReplaced: () => void;
}

interface SetPasswordSubmitState {
  readonly busy: boolean;
  readonly error: SetPasswordError | null;
  readonly setError: (next: SetPasswordError | null) => void;
  readonly submit: () => Promise<void>;
  readonly supersede: () => void;
}

interface SetPasswordAttempt {
  readonly saved: boolean;
  readonly error: SetPasswordError | null;
}

/** POST + classify. Always reports unexpected throwables; the caller decides state. */
async function attemptSetPassword(
  config: AppConfig,
  password: string,
): Promise<SetPasswordAttempt> {
  try {
    const saved = confirmsSet(await postAuthJson(config, 'password', { password }));
    return { saved, error: saved ? null : 'generic' };
  } catch (err) {
    return { saved: false, error: failureFor(err) };
  }
}

/**
 * Owns the transient submit state and the POST itself.
 *
 * `inFlight` is a REF, not the `busy` state, and that is what makes a double
 * submit one POST: `disabled={busy}` protects the button only — Enter and any
 * form-level submit event bypass it — and a guard reading `busy` reads a render
 * closure, so two submits batched into one React task both see `false`. The
 * cost of getting it wrong here is two writes of the credential for one user
 * action.
 *
 * `mounted` guards the state writes if `SetPassword` itself unmounts (sign-out).
 * Cancel no longer unmounts this hook — it lives on the parent — but a late
 * settlement after dismiss is still ignored via `generation`.
 */
export function useSetPasswordSubmit(opts: SetPasswordSubmitOpts): SetPasswordSubmitState {
  const { config, credential, onSaved, onReplaced } = opts;
  const { password, setPassword } = credential;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<SetPasswordError | null>(null);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const supersede = useCallback(() => {
    generation.current += 1;
  }, []);

  const submit = useCallback(async () => {
    await runSetPasswordSubmit({
      config,
      password,
      setPassword,
      onSaved,
      onReplaced,
      inFlight,
      generation,
      mounted,
      setBusy,
      setError,
    });
  }, [config, onSaved, onReplaced, password, setPassword]);

  return { busy, error, setError, submit, supersede };
}

interface RunSetPasswordSubmitOpts {
  readonly config: AppConfig;
  readonly password: string;
  readonly setPassword: (next: string) => void;
  readonly onSaved: () => void;
  readonly onReplaced: () => void;
  readonly inFlight: MutableRefObject<boolean>;
  readonly generation: MutableRefObject<number>;
  readonly mounted: MutableRefObject<boolean>;
  readonly setBusy: (next: boolean) => void;
  readonly setError: (next: SetPasswordError | null) => void;
}

async function runSetPasswordSubmit(args: RunSetPasswordSubmitOpts): Promise<void> {
  const {
    config,
    password,
    setPassword,
    onSaved,
    onReplaced,
    inFlight,
    generation,
    mounted,
    setBusy,
    setError,
  } = args;
  if (inFlight.current) return;
  setError(null);
  const invalid = validatePassword(password);
  if (invalid !== null) {
    setError(invalid);
    return;
  }
  const started = generation.current;
  inFlight.current = true;
  setBusy(true);
  const result = await attemptSetPassword(config, password);
  inFlight.current = false;
  if (!mounted.current) return;
  setBusy(false);
  if (started !== generation.current) {
    /* Dismissed while this was out. Cancel revoked nothing, so a save that
       landed anyway replaced the credential and is reported as such — as a
       replacement, never as the confirmation of an attempt the user backed
       out of. Everything below belongs to the attempt that is no longer live. */
    if (result.saved) onReplaced();
    return;
  }
  if (result.saved) {
    clearPasswordSync(setPassword);
    onSaved();
    return;
  }
  if (result.error !== null) {
    if (spendsValue(result.error)) clearPasswordSync(setPassword);
    setError(result.error);
  }
}
